import React from 'react';
import { Link } from 'react-router-dom';

const SecurityPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <nav className="bg-emerald-900 text-white sticky top-0 z-50 shadow-lg">
        <div className="container mx-auto max-w-6xl px-6 flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-700 shadow-inner">
              <span className="text-xl">🏠</span>
            </div>
            <span className="font-bold text-lg tracking-tight">Landlord Pro</span>
          </Link>
        </div>
      </nav>

      <header className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-slate-900 text-white pt-16 pb-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaC0xMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50"></div>
        <div className="relative max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Security</h1>
          <p className="text-emerald-100 text-lg md:text-xl max-w-2xl mx-auto">
            How Landlord Pro protects your data — by Compliance Engine Ltd
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16 space-y-12">
        <p className="text-sm text-slate-400 border-b border-slate-200 pb-4">
          Last updated: 20 June 2026
        </p>

        <section>
          <h2 className="text-2xl font-bold mb-4">1. Authentication & Access Control</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            User identity is managed by <strong>AWS Cognito</strong> with separate pools for production and staging. We never store passwords ourselves — Cognito handles password hashing, complexity enforcement, and optional multi-factor (TOTP / SMS) flows.
          </p>
          <h3 className="font-semibold mb-2">Token signing</h3>
          <p className="text-slate-600 leading-relaxed mb-4">
            End-user sessions are bearer JSON Web Tokens issued by Cognito and verified on every request via <code className="bg-slate-100 text-emerald-700 px-1 rounded">aws-jwt-verify</code> against the user pool's public JWKS. API Gateway uses a Cognito authorizer on every endpoint; no unauthenticated access is permitted except for the FastSpring webhook, which is authenticated via HMAC.
          </p>
          <h3 className="font-semibold mb-2">Authorisation</h3>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            <li><strong>Tenant isolation:</strong> every authenticated request resolves a <code className="bg-slate-100 text-emerald-700 px-1 rounded">tenantId</code> from the verified JWT. Data-access helpers refuse to query without it, preventing cross-tenant reads.</li>
            <li><strong>Privileged roles:</strong> SuperAdmin status is granted strictly by Cognito group membership — never by email address pattern, domain, or other inferred signal.</li>
            <li><strong>API-level gating:</strong> the SuperAdmin Lambda middleware checks <code className="bg-slate-100 text-emerald-700 px-1 rounded">cognito:groups</code> from the verified JWT before allowing access to any admin endpoint.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">2. Data Protection</h2>
          <h3 className="font-semibold mb-2">Encryption at rest</h3>
          <p className="text-slate-600 leading-relaxed mb-4">
            <strong>DynamoDB:</strong> all multi-tenant data, including compliance records, property details, tenancy agreements, and user metadata, encrypted with AWS-managed keys (AES-256). <strong>S3:</strong> uploaded compliance documents, certificates, and evidence files stored with server-side encryption (SSE-S3) on private buckets with public access blocked.
          </p>
          <h3 className="font-semibold mb-2">Encryption in transit</h3>
          <p className="text-slate-600 leading-relaxed mb-4">
            HTTPS is enforced end-to-end. Strict-Transport-Security with preload is sent on every response. All API traffic uses TLS 1.2 or higher; no plaintext endpoints are exposed.
          </p>
          <h3 className="font-semibold mb-2">Data residency</h3>
          <p className="text-slate-600 leading-relaxed">
            All production data is processed and stored in the AWS London region (<code className="bg-slate-100 text-emerald-700 px-1 rounded">eu-west-2</code>). Sub-processors (FastSpring, Brevo) carry their own UK / EU adequacy or IDTA arrangements.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">3. Application Security</h2>
          <h3 className="font-semibold mb-2">CORS allowlist</h3>
          <p className="text-slate-600 leading-relaxed mb-4">
            API accepts requests only from explicitly enumerated origins — the Azure Static Web Apps we control, our custom domain, and localhost for development. Wildcard or substring matching is intentionally not used.
          </p>
          <h3 className="font-semibold mb-2">Rate limiting</h3>
          <p className="text-slate-600 leading-relaxed mb-4">
            Public, authentication, and webhook endpoints are capped per-IP. Authentication-sensitive routes have a stricter ceiling than general read endpoints.
          </p>
          <h3 className="font-semibold mb-2">Input handling</h3>
          <p className="text-slate-600 leading-relaxed mb-4">
            Every model uses a field allowlist (<code className="bg-slate-100 text-emerald-700 px-1 rounded">pick</code>) to filter request bodies before persistence, preventing mass-assignment of unintended attributes.
          </p>
          <h3 className="font-semibold mb-2">Dependency hygiene</h3>
          <p className="text-slate-600 leading-relaxed">
            Production <code className="bg-slate-100 text-emerald-700 px-1 rounded">npm audit</code> is monitored. Critical or high-severity advisories trigger an unscheduled patch deploy.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">4. Webhook & Integration Security</h2>
          <p className="text-slate-600 leading-relaxed">
            FastSpring is our subscription / billing integration. All FastSpring webhooks are authenticated via HMAC-SHA256 signature of the raw request body against a shared secret configured in both the FastSpring dashboard and our deployment environment. Requests without a valid signature receive HTTP 401 and are not processed. The FastSpring secret is loaded at Lambda boot via <code className="bg-slate-100 text-emerald-700 px-1 rounded">requireSecret()</code> — the runtime fails fast if it is missing rather than falling back to any hardcoded value.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">5. Browser Security Headers</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            Every page served from our application sends the following security headers:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            <li><strong>Content-Security-Policy</strong> — narrow <code className="bg-slate-100 text-emerald-700 px-1 rounded">default-src 'self'</code> with explicit allowlists for our API gateway, Cognito, FastSpring checkout, and Google Analytics.</li>
            <li><strong>X-Frame-Options: DENY</strong> — the application cannot be embedded in any external iframe, preventing clickjacking.</li>
            <li><strong>Permissions-Policy</strong> — disables camera, microphone, geolocation, USB, magnetometer, accelerometer, gyroscope, and FLoC by default.</li>
            <li><strong>Referrer-Policy: strict-origin-when-cross-origin</strong> — limits information leaked on outbound navigation.</li>
            <li><strong>Cross-Origin-Opener-Policy: same-origin-allow-popups</strong> — prevents cross-origin window manipulation.</li>
            <li><strong>Cross-Origin-Resource-Policy: same-origin</strong> — restricts resource loading to same-origin requests.</li>
            <li><strong>Cross-Origin-Embedder-Policy: require-corp</strong> — ensures cross-origin resources explicitly opt in.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">6. Audit & Monitoring</h2>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            <li><strong>Application logs:</strong> structured JSON to CloudWatch Logs with per-stage log groups; retention is configured per environment.</li>
            <li><strong>Domain audit trail:</strong> sensitive actions (subscription tier changes, user role updates, tenant configuration) are recorded as an immutable, tenant-scoped log.</li>
            <li><strong>Infrastructure observability:</strong> CloudWatch metrics on API Gateway and Lambda track latency and error rates; failed deploys block the release pipeline.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">7. Standards Alignment</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            We align our engineering practice with the <strong>OWASP Top 10 (2021)</strong> risk categories. We maintain an internal scorecard covering each item:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ['A01 Broken Access Control', 'Tenant-isolated queries, group-based privilege grants'],
              ['A02 Cryptographic Failures', 'HSTS, TLS 1.2+, required boot-time secrets'],
              ['A03 Injection', 'Parameterised DynamoDB ExpressionAttributeValues throughout'],
              ['A04 Insecure Design', 'Rate limiting, body size caps, allowlist input filtering'],
              ['A05 Security Misconfiguration', 'CSP, locked-down CORS, security headers'],
              ['A06 Vulnerable Components', 'Zero production CVEs targeted'],
              ['A07 Identification & Auth', 'Cognito MFA available, no hardcoded fallbacks'],
              ['A08 Software & Data Integrity', 'Signed webhooks, HMAC verification'],
              ['A09 Security Logging', 'CloudWatch + audit trail for security-relevant events'],
              ['A10 SSRF', 'No user-controlled outbound URL fetches in user code paths'],
            ].map(([item, desc]) => (
              <div key={item} className="bg-white rounded-lg border border-slate-200 p-4">
                <h3 className="font-semibold text-sm text-emerald-700 mb-1">{item}</h3>
                <p className="text-xs text-slate-500">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">8. Responsible Disclosure</h2>
          <p className="text-slate-600 leading-relaxed mb-4">
            We welcome reports of suspected security vulnerabilities. If you believe you have found an issue affecting Landlord Pro, please contact us.
          </p>
          <h3 className="font-semibold mb-2">What to include in a report</h3>
          <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4">
            <li>A description of the issue, including the URL or component affected</li>
            <li>Steps to reproduce, with any proof-of-concept code or screenshots</li>
            <li>Your assessment of impact</li>
            <li>Your contact details if you'd like a response</li>
          </ul>
          <h3 className="font-semibold mb-2">Our commitments</h3>
          <ul className="list-disc list-inside text-slate-600 space-y-1 mb-4">
            <li>We will acknowledge receipt of your report within two working days.</li>
            <li>We will keep you informed of remediation progress on material findings.</li>
            <li>We will not pursue legal action against good-faith researchers acting within scope.</li>
            <li>With your consent, we will credit you in acknowledgements once a fix has shipped.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4">9. Contact</h2>
          <p className="text-slate-600 leading-relaxed">
            Security questions, compliance questionnaires, or vulnerability reports:{' '}
            <a href="mailto:hello@compliance-engine.io" className="text-emerald-600 hover:underline font-medium">
              hello@compliance-engine.io
            </a>
          </p>
        </section>
      </main>

      <footer className="bg-slate-900 text-slate-400 py-10 px-6">
        <div className="max-w-3xl mx-auto text-center text-sm space-y-2">
          <p>&copy; 2026 Compliance Engine Ltd. All rights reserved.</p>
          <div className="flex justify-center gap-6">
            <a href="/privacy-policy.html" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms-of-service.html" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="/security" className="text-emerald-400 hover:text-emerald-300 transition-colors">Security</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SecurityPage;
